import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { initializeApiInterceptors } from './axiosInterceptors';
import { HttpClient } from './httpClient';

export type ResponseHandler<T = any, R = T> = (response: AxiosResponse<T>) => R;

export interface ApiErrorPayload {
    code: number;
    message: string;
    data: unknown;
    response: AxiosResponse;
    [key: string]: unknown;
}

export function getApiErrorStatus(error: unknown): number | undefined {
    if (typeof error !== 'object' || error === null) {
        return undefined;
    }

    const payload = error as ApiErrorPayload;
    if (typeof payload.code === 'number') {
        return payload.code;
    }

    return payload.response?.status;
}

export function isAuthApiError(error: unknown): boolean {
    const status = getApiErrorStatus(error);

    return status === 401 || status === 403;
}

function getResponseContentType(response: AxiosResponse): string {
    return String(response.headers['content-type'] ?? '');
}

function decodeArrayBuffer(data: ArrayBuffer): unknown {
    try {
        const text = new TextDecoder().decode(data);

        if (!text.trim()) {
            return null;
        }

        return JSON.parse(text);
    } catch {
        return null;
    }
}

function normalizeErrorPayload(response: AxiosResponse): ApiErrorPayload {
    const rawData =
        response.data instanceof ArrayBuffer &&
        getResponseContentType(response).includes('application/json')
            ? decodeArrayBuffer(response.data)
            : response.data;
    const rawObject =
        typeof rawData === 'object' && rawData !== null
            ? (rawData as Record<string, unknown>)
            : {};
    const code =
        typeof rawObject.code === 'number' ? rawObject.code : response.status;
    const message =
        typeof rawObject.message === 'string' && rawObject.message.trim()
            ? rawObject.message
            : response.statusText || `Request failed with status ${code}`;

    return {
        ...rawObject,
        code,
        message,
        data: rawData,
        response,
    };
}

function statusChecker<T>(response: AxiosResponse<T>) {
    if (response.status >= 200 && response.status < 300) {
        return response;
    }

    throw normalizeErrorPayload(response);
}

class ApiService {
    private _httpClient = HttpClient.getInstance();
    constructor() {
        initializeApiInterceptors(this._httpClient);
    }

    responseHandler = <T = any>({ data }: AxiosResponse<T>) => data;

    async get<T = any>(
        url: string,
        config?: AxiosRequestConfig
    ): Promise<AxiosResponse<T>> {
        return statusChecker(await this._httpClient.get<T>(url, config));
    }

    async post<T = any, B = any>(
        url: string,
        body: B
    ): Promise<AxiosResponse<T>> {
        return statusChecker(await this._httpClient.post<T>(url, body));
    }

    async put<T = any, B = any>(
        url: string,
        body: B
    ): Promise<AxiosResponse<T>> {
        return statusChecker(await this._httpClient.put<T>(url, body));
    }

    async delete(url: string): Promise<AxiosResponse<null>> {
        return statusChecker(await this._httpClient.delete(url));
    }
}

export const apiService = new ApiService();
